/**
 * Sync Progress Toast Component
 * Displays progress indicator for Basecamp sync operations using Sonner
 */

import { toast } from 'sonner';
import { StorageKeys } from '@/lib/storage';

interface SyncProgressController {
  update: (progress: number, message: string) => void;
  complete: () => void;
  error: (error: string) => void;
  cancel: () => void;
}

export interface SyncProgressOptions {
  onPause?: () => void;
  onRestart?: () => void;
}

/** Called when sync is in progress; Cancel stops the in-flight fetch, Restart starts a new sync */
export function showSyncProgress(options?: SyncProgressOptions | (() => void)): SyncProgressController {
  const onPause = typeof options === 'function' ? options : options?.onPause;
  const onRestart = typeof options === 'object' && options?.onRestart ? options.onRestart : undefined;

  const handleCancel = () => {
    try {
      localStorage.setItem(StorageKeys.BASECAMP_SYNC_PAUSED, 'true');
    } catch (error) {
      console.warn('[BasecampSync] Failed to save pause flag:', error);
    }
    onPause?.(); // Abort in-flight fetch
    toast.info('Sync was cancelled. Existing data is preserved.', {
      duration: Infinity,
      action: onRestart
        ? {
            label: 'Restart sync',
            onClick: () => {
              try {
                localStorage.removeItem(StorageKeys.BASECAMP_SYNC_PAUSED);
              } catch {
                // ignore
              }
              onRestart();
            },
          }
        : undefined,
    });
  };

  const toastId = toast.loading('Syncing Basecamp data...', {
    description: 'Fetching projects and todos',
    duration: Infinity, // Keep open until explicitly dismissed
    action: {
      label: 'Cancel sync',
      onClick: handleCancel,
    },
  });

  return {
    update: (progress: number, message: string) => {
      toast.loading(message, {
        id: toastId,
        description: `${Math.round(progress)}% complete`,
        duration: Infinity,
        action: {
          label: 'Cancel sync',
          onClick: handleCancel,
        },
      });
    },
    
    complete: () => {
      toast.success('Sync complete!', { 
        id: toastId,
        description: 'All Basecamp data is up to date',
        duration: 3000,
      });
    },
    
    error: (error: string) => {
      toast.error('Sync failed', {
        id: toastId,
        description: error,
        duration: 5000,
        action: {
          label: 'Retry',
          onClick: () => {
            // Trigger a page reload to restart sync
            window.location.reload();
          },
        },
      });
    },
    
    cancel: () => {
      toast.dismiss(toastId);
    },
  };
}

export function showResumePrompt(onRestart: () => void) {
  toast.info('Sync was cancelled. Existing data is preserved.', {
    duration: Infinity,
    action: {
      label: 'Restart sync',
      onClick: () => {
        try {
          localStorage.removeItem(StorageKeys.BASECAMP_SYNC_PAUSED);
        } catch (error) {
          console.warn('[BasecampSync] Failed to remove pause flag:', error);
        }
        onRestart();
      },
    },
  });
}

export function checkPausedSync(): boolean {
  try {
    return localStorage.getItem(StorageKeys.BASECAMP_SYNC_PAUSED) === 'true';
  } catch (error) {
    console.warn('[BasecampSync] Failed to check pause flag:', error);
    return false;
  }
}

export function clearPausedSync() {
  try {
    localStorage.removeItem(StorageKeys.BASECAMP_SYNC_PAUSED);
  } catch (error) {
    console.warn('[BasecampSync] Failed to clear pause flag:', error);
  }
}

